import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  qualityEquipmentService,
  type QualityEquipmentAttachmentRecord,
  type QualityEquipmentPayload,
  type QualityEquipmentRecord,
  type QualityEquipmentStatus,
} from '@/services/quality-equipment.service';

const { Title, Text } = Typography;
const { Search } = Input;

interface QualityEquipmentFormValues {
  equipment_manufacturer?: string;
  identification: string;
  equipment_description?: string;
  serial_number?: string;
  calibration_period?: number | null;
  last_calibration_date?: dayjs.Dayjs | null;
  calibration_due_date?: dayjs.Dayjs | null;
  status: QualityEquipmentStatus;
  utilized_person?: string;
}

const emptyToNull = (value?: string): string | null => {
  const next = value?.trim();
  return next ? next : null;
};

const mapFormToPayload = (values: QualityEquipmentFormValues): QualityEquipmentPayload => ({
  equipment_manufacturer: emptyToNull(values.equipment_manufacturer),
  identification: values.identification.trim(),
  equipment_description: emptyToNull(values.equipment_description),
  serial_number: emptyToNull(values.serial_number),
  calibration_period:
    values.calibration_period == null || values.calibration_period === undefined
      ? null
      : Number(values.calibration_period),
  last_calibration_date: values.last_calibration_date
    ? values.last_calibration_date.format('YYYY-MM-DD')
    : null,
  calibration_due_date: values.calibration_due_date
    ? values.calibration_due_date.format('YYYY-MM-DD')
    : null,
  status: values.status,
  utilized_person: emptyToNull(values.utilized_person),
});

function getStatusColor(status: QualityEquipmentStatus): string {
  const colors: Record<QualityEquipmentStatus, string> = {
    Active: 'success',
    Inactive: 'default',
    Due: 'warning',
  };
  return colors[status] || 'default';
}

const QualityEquipmentsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<QualityEquipmentFormValues>();
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<QualityEquipmentRecord | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentDetails, setAttachmentDetails] = useState('');
  const [attachmentExpiryDate, setAttachmentExpiryDate] = useState<dayjs.Dayjs | null>(null);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['quality', 'equipments', searchText],
    queryFn: async () => {
      const response = await qualityEquipmentService.getAll(searchText);
      return response.data?.data || response.data || [];
    },
  });

  const { data: editingEquipmentDetail, isLoading: isLoadingAttachments } = useQuery({
    queryKey: ['quality', 'equipment-detail', editingRecord?.id],
    queryFn: async () => {
      const response = await qualityEquipmentService.getById(editingRecord!.id);
      return (response.data?.data || response.data) as QualityEquipmentRecord;
    },
    enabled: !!editingRecord?.id && isModalOpen,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: QualityEquipmentPayload) => {
      const response = await qualityEquipmentService.create(payload);
      return response.data?.data || response.data;
    },
    onSuccess: (saved: QualityEquipmentRecord) => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'equipments'] });
      queryClient.invalidateQueries({ queryKey: ['quality', 'equipment-detail', saved.id] });
      setEditingRecord(saved);
      message.success("Equipment saved. You can now add document attachments.");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to create Equipment's");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: QualityEquipmentPayload }) => {
      const response = await qualityEquipmentService.update(id, payload);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'equipments'] });
      message.success("Equipment's updated successfully");
      setIsModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to update Equipment's");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await qualityEquipmentService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'equipments'] });
      message.success("Equipment's deleted successfully");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to delete Equipment's");
    },
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: async (vars: {
      equipmentId: string;
      file: File;
      details?: string | null;
      expiry_date?: string | null;
    }) => {
      const response = await qualityEquipmentService.uploadAttachment(vars.equipmentId, vars.file, {
        details: vars.details,
        expiry_date: vars.expiry_date,
      });
      return response.data?.data || response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'equipment-detail', variables.equipmentId] });
      setAttachmentFile(null);
      setAttachmentDetails('');
      setAttachmentExpiryDate(null);
      message.success('Attachment added successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to add attachment');
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      await qualityEquipmentService.deleteAttachment(attachmentId);
    },
    onSuccess: () => {
      if (editingRecord?.id) {
        queryClient.invalidateQueries({ queryKey: ['quality', 'equipment-detail', editingRecord.id] });
      }
      message.success('Attachment removed');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to remove attachment');
    },
  });

  const columns: ColumnsType<QualityEquipmentRecord> = useMemo(
    () => [
      {
        title: 'Identification',
        dataIndex: 'identification',
        key: 'identification',
        width: 150,
      },
      {
        title: 'Manufacturer',
        dataIndex: 'equipment_manufacturer',
        key: 'equipment_manufacturer',
        width: 180,
        render: (value?: string | null) => value || '—',
      },
      {
        title: 'Description',
        dataIndex: 'equipment_description',
        key: 'equipment_description',
      },
      {
        title: 'Serial Number',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 150,
        render: (value?: string | null) => value || '—',
      },
      {
        title: 'Calibration Period',
        dataIndex: 'calibration_period',
        key: 'calibration_period',
        width: 140,
        render: (value?: number | null) => (value != null ? `${value}` : '—'),
      },
      {
        title: 'Due Date',
        dataIndex: 'calibration_due_date',
        key: 'calibration_due_date',
        width: 130,
        render: (value?: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : '—'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (value: QualityEquipmentStatus) => <Tag color={getStatusColor(value)}>{value}</Tag>,
      },
      {
        title: 'Utilized Person',
        dataIndex: 'utilized_person',
        key: 'utilized_person',
        width: 160,
        render: (value?: string | null) => value || '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingRecord(record);
                form.setFieldsValue({
                  equipment_manufacturer: record.equipment_manufacturer || undefined,
                  identification: record.identification,
                  equipment_description: record.equipment_description || undefined,
                  serial_number: record.serial_number || undefined,
                  calibration_period: record.calibration_period ?? undefined,
                  last_calibration_date: record.last_calibration_date
                    ? dayjs(record.last_calibration_date)
                    : null,
                  calibration_due_date: record.calibration_due_date
                    ? dayjs(record.calibration_due_date)
                    : null,
                  status: record.status,
                  utilized_person: record.utilized_person || undefined,
                });
                setIsModalOpen(true);
              }}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete Equipment's record?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteMutation.mutate(record.id)}
            >
              <Button danger size="small" icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteMutation.isPending, form]
  );

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setAttachmentFile(null);
    setAttachmentDetails('');
    setAttachmentExpiryDate(null);
    form.setFieldsValue({
      status: 'Active',
    });
    setIsModalOpen(true);
  };

  const attachmentColumns: ColumnsType<QualityEquipmentAttachmentRecord> = [
    {
      title: 'File',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (value: string, row) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          icon={<EyeOutlined />}
          onClick={() => window.open(row.file_url, '_blank', 'noopener,noreferrer')}
        >
          {value}
        </Button>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (value?: string | null) => value || '—',
    },
    {
      title: 'Expiry date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 140,
      render: (value?: string | null) => (value ? dayjs(value).format('DD-MM-YYYY') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Popconfirm
          title="Delete attachment?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => deleteAttachmentMutation.mutate(row.id)}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={deleteAttachmentMutation.isPending}
          >
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Equipment&apos;s
          </Title>
          <Text type="secondary">
            Maintain the Quality equipment register with calibration and utilization details.
          </Text>
        </div>

        <Card>
          <Space
            wrap
            style={{
              marginBottom: 16,
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Search
              allowClear
              placeholder="Search identification, manufacturer, serial number, utilized person..."
              style={{ width: 340 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add Equipment&apos;s
            </Button>
          </Space>

          <Table<QualityEquipmentRecord>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={records}
            scroll={{ x: 1300 }}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          />
        </Card>
      </Space>

      <Modal
        title={editingRecord ? "Edit Equipment's" : "Add Equipment's"}
        open={isModalOpen}
        width={900}
        okText={editingRecord ? 'Update' : 'Create'}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingRecord(null);
          setAttachmentFile(null);
          setAttachmentDetails('');
          setAttachmentExpiryDate(null);
          form.resetFields();
        }}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        onOk={() => form.submit()}
      >
        <Form<QualityEquipmentFormValues>
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            const payload = mapFormToPayload(values);
            if (editingRecord) {
              await updateMutation.mutateAsync({ id: editingRecord.id, payload });
            } else {
              await createMutation.mutateAsync(payload);
            }
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="equipment_manufacturer" label="Equipment Manufacturer">
                <Input placeholder="Manufacturer name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="identification"
                label="Identification"
                rules={[{ required: true, message: 'Please enter equipment ID' }]}
              >
                <Input placeholder="Equipment ID" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="equipment_description" label="Equipment Description">
                <Input placeholder="Description" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="serial_number" label="Serial Number">
                <Input placeholder="Serial number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_period" label="Calibration Period">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Calibration period" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="last_calibration_date" label="Last Calibration Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_due_date" label="Calibration Due Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select
                  options={[
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                    { value: 'Due', label: 'Due' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="utilized_person" label="Utilized Person">
                <Input placeholder="Utilized person" />
              </Form.Item>
            </Col>
          </Row>

          <Card
            size="small"
            title="Document attachments"
            style={{ marginTop: 8 }}
          >
            {!editingRecord?.id ? (
              <Alert
                type="info"
                showIcon
                message="Save the equipment first to add attachments"
              />
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Row gutter={[16, 16]} align="bottom">
                  <Col xs={24} md={10}>
                    <Text style={{ display: 'block', marginBottom: 8 }}>File</Text>
                    <Upload
                      maxCount={1}
                      beforeUpload={(file) => {
                        setAttachmentFile(file);
                        return false;
                      }}
                      onRemove={() => {
                        setAttachmentFile(null);
                      }}
                      fileList={
                        attachmentFile
                          ? [{ uid: 'equipment-attachment', name: attachmentFile.name, status: 'done' }]
                          : []
                      }
                      style={{ width: '100%' }}
                    >
                      <Button icon={<UploadOutlined />} style={{ width: '100%' }}>
                        Choose File
                      </Button>
                    </Upload>
                  </Col>
                  <Col xs={24} md={7}>
                    <Text style={{ display: 'block', marginBottom: 8 }}>Details</Text>
                    <Input
                      placeholder="Description of document"
                      value={attachmentDetails}
                      onChange={(e) => setAttachmentDetails(e.target.value)}
                    />
                  </Col>
                  <Col xs={24} md={5}>
                    <Text style={{ display: 'block', marginBottom: 8 }}>Expiry date</Text>
                    <DatePicker
                      style={{ width: '100%' }}
                      format="DD-MM-YYYY"
                      placeholder="dd-mm-yyyy"
                      value={attachmentExpiryDate}
                      onChange={setAttachmentExpiryDate}
                    />
                  </Col>
                  <Col xs={24}>
                    <Button
                      type="primary"
                      disabled={!attachmentFile}
                      loading={uploadAttachmentMutation.isPending}
                      style={{ minWidth: 168 }}
                      onClick={async () => {
                        if (!editingRecord?.id || !attachmentFile) return;
                        await uploadAttachmentMutation.mutateAsync({
                          equipmentId: editingRecord.id,
                          file: attachmentFile,
                          details: attachmentDetails.trim() || null,
                          expiry_date: attachmentExpiryDate
                            ? attachmentExpiryDate.format('YYYY-MM-DD')
                            : null,
                        });
                      }}
                    >
                      Add attachment
                    </Button>
                  </Col>
                </Row>

                <Table<QualityEquipmentAttachmentRecord>
                  rowKey="id"
                  size="small"
                  pagination={false}
                  loading={isLoadingAttachments}
                  columns={attachmentColumns}
                  dataSource={editingEquipmentDetail?.attachments || []}
                  locale={{ emptyText: 'No attachments' }}
                />
              </Space>
            )}
          </Card>
        </Form>
      </Modal>
    </div>
  );
};

export default QualityEquipmentsPage;
